import type { AriaAttributes } from 'react';
import { useMemo, memo } from 'react';
import { HStack } from '../layout';
import { Text } from '../typography';
import PaginationButton from './PaginationButton';
import computePaginationItems, {
    generatePageRange,
} from './util/computePaginationItems';
import styles from './PageNavigator.module.scss';

interface PageNavigatorProps extends AriaAttributes {
    /**
     * Current active page
     */
    page: number;
    /**
     * Total number of pages
     */
    count: number;
    onChange(value: number): void;
}

export const PaginationTopAnchor = memo(() => {
    return <div id="pagination-top-anchor-element" />;
});

const PaginationList = memo(({ page, count, onChange }: PageNavigatorProps) => {
    const items = useMemo(
        () => computePaginationItems({ pageCount: count, page, onChange }),
        [count, onChange, page]
    );

    return (
        <>
            {items.map((item, idx) => {
                if (item.type.includes('ellipsis')) {
                    return (
                        <div
                            key={'ellipsis_' + idx}
                            className={styles.ellipses}
                            data-test-class="paginationSeparatorEllipses"
                        >
                            <Text secondary regular>
                                …
                            </Text>
                        </div>
                    );
                }

                if (item.type === 'page') {
                    const value = item.page as number;

                    return (
                        <PaginationButton
                            key={value}
                            page={page}
                            value={value}
                            onChange={onChange}
                        />
                    );
                }

                return null;
            })}
        </>
    );
});

const PageNavigator = ({
    autoScroll = false,
    page,
    count,
    onChange,
}: PageNavigatorProps & {
    /**
     * Reset scroll to list top when changing page.
     */
    autoScroll?: boolean;
}) => {
    if (!count || count === 1) {
        return null;
    }

    const onPageChange = (value: number) => {
        onChange(value);

        if (page !== value && autoScroll) {
            const element =
                document.getElementById('pagination-top-anchor-element') ||
                document.querySelector('body');

            if (!element) {
                return;
            }

            element.scrollIntoView(true);
        }
    };

    return (
        <nav data-test-class="paginationNavigation">
            <HStack align="center" gap="micro">
                <PaginationButton
                    previous
                    value={page - 1}
                    disabled={page === 1}
                    page={page}
                    onChange={onPageChange}
                />
                {count <= 5 ? (
                    generatePageRange(1, count).map(pageValue => (
                        <PaginationButton
                            key={pageValue}
                            page={page}
                            value={pageValue}
                            onChange={onPageChange}
                        />
                    ))
                ) : (
                    <PaginationList
                        page={page}
                        count={count}
                        onChange={onPageChange}
                    />
                )}
                <PaginationButton
                    next
                    value={page + 1}
                    disabled={page === count}
                    page={page}
                    onChange={onPageChange}
                />
            </HStack>
        </nav>
    );
};

export default memo(PageNavigator);
